import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { CalendarIcon } from '../../icons';
import ValidationErrorText from '../common/ValidationErrorText';

const ApplicationPeriodPicker = ({
  title = 'Admission Process',
  label = 'Application Period',
  value = { start: null, end: null },
  onChange = () => {},
  readOnly = false,
  minDate = null,
  maxDate = null,
  fieldErrors,
  fieldError,
  fieldType
}) => {
  const startDate = value?.start || null;
  const endDate = value?.end || null;

  const setRange = (nextStart, nextEnd) => onChange({ start: nextStart, end: nextEnd });

  const handleStartChange = (newStart) => {
    if (endDate && newStart && moment(endDate).isBefore(newStart, 'day')) {
      setRange(newStart, null);
    } else {
      setRange(newStart, endDate);
    }
  };

  const handleEndChange = (newEnd) => {
    if (startDate && newEnd && moment(newEnd).isBefore(startDate, 'day')) {
      setRange(startDate, startDate); 
    } else {
      setRange(startDate, newEnd);
    }
  };

  const commonTextFieldProps = (params) => ({
    ...params,
    placeholder: 'DD/MM/YY',
    size: 'small',
    InputProps: {
      ...params.InputProps,
      sx: {
        height: 38,
        minHeight: 38,
        '& input': {
          padding: '8px 14px',
          height: 22,
          boxSizing: 'border-box',
          fontSize: 13,
          lineHeight: '22px',
        },
      },
    },
    inputProps: {
      ...params.inputProps,
      style: { fontSize: 13 },
    },
  });

  return (
    <>
      <Typography fontSize="16px" fontWeight="600" mb={1}>
        {title}
      </Typography>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}
      </Typography>

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {/* Start Date */}
          <Box>
          <DatePicker
            format="DD/MM/YY"
            value={startDate}
            onChange={handleStartChange}
            disabled={readOnly}
            minDate={minDate}
            maxDate={maxDate}
            slots={{ openPickerIcon: CalendarIcon }}
            renderInput={(params) => (
              <Box
                onClick={() => params.inputProps?.onClick?.()}
                sx={{ width: 140, cursor: readOnly ? 'default' : 'pointer' }}
              >
                <TextField {...commonTextFieldProps(params)} />
              </Box>
            )}
            sx={{
              minWidth: '140px',
              maxWidth: '140px',
              '& .MuiPickersSectionList-root': {
                padding: '10px 0px',
                height: 38,
                boxSizing: 'border-box',
                fontSize: 13,
              },
            }}
          />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}.start`]}/>
          </Box>

          <Typography sx={{ fontSize: '13px' }}>To</Typography>

          {/* End Date */}
          <Box>
          <DatePicker
            format="DD/MM/YY"
            value={endDate}
            onChange={handleEndChange}
            disabled={readOnly}
            minDate={startDate || minDate} // cannot be before start
            maxDate={maxDate}
            slots={{ openPickerIcon: CalendarIcon }}
            renderInput={(params) => (
              <Box
                onClick={() => params.inputProps?.onClick?.()}
                sx={{ width: 140, cursor: readOnly ? 'default' : 'pointer' }}
              >
                <TextField {...commonTextFieldProps(params)} />
              </Box>
            )}
            sx={{
              minWidth: '140px',
              maxWidth: '140px',
              '& .MuiPickersSectionList-root': {
                padding: '10px 0px',
                height: 38,
                boxSizing: 'border-box',
                fontSize: 13,
              },
            }}
          />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}.end`]}/>
          </Box>

          {startDate && endDate && (
            <Typography sx={{ color: '#6c757d', fontSize: '13px' }}>
              From {moment(startDate).format('DD MMMM YYYY')} to {moment(endDate).format('DD MMMM YYYY')}
            </Typography>
          )}
        </Box>
      </LocalizationProvider>
    </>
  );
};

export default ApplicationPeriodPicker;
